import { authenticatedFetch, getAuthToken } from "../api";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

export const JOB_MODES = {
  EXACT: "exact_uploaded",
  AI_AROUND: "around_uploaded",
  RANDOM: "random_ai"
};

export const JOB_MODE_LABELS = {
  [JOB_MODES.EXACT]: "Exact Uploaded Questions",
  [JOB_MODES.AI_AROUND]: "AI Around Uploaded Questions",
  [JOB_MODES.RANDOM]: "Fully Random AI Questions"
};

export const EVENT_TYPES = {
  INTERVIEW: "INTERVIEW",
  MCQ: "MCQ",
  CODING: "CODING"
};

export const EVENT_TYPE_LABELS = {
  [EVENT_TYPES.INTERVIEW]: "AI Interview",
  [EVENT_TYPES.MCQ]: "MCQ Test",
  [EVENT_TYPES.CODING]: "Coding Test"
};

export function buildJobId() {
  const stamp = Date.now().toString(36).toUpperCase();
  const suffix = Math.random().toString(36).slice(2, 7).toUpperCase();
  return `JOB-${stamp}-${suffix}`;
}

export function parseQuestions(rawQuestions) {
  return rawQuestions
    .split("\n")
    .map((question) => question.trim())
    .filter(Boolean);
}

export function buildJobPayload(
  { title, companyName, mode, uploadedQuestions, eventType, config, pipelineStages, stageConfig, resumeCriteria },
  user
) {
  const questions = Array.isArray(uploadedQuestions)
    ? uploadedQuestions
    : parseQuestions(uploadedQuestions || "");

  return {
    title: title.trim(),
    companyName: companyName.trim(),
    mode,
    uploadedQuestions: questions,
    // eventType is required by the backend for legacy validation, but a
    // pipelineStages job is branched on pipelineStages (not eventType) by the
    // dashboard, so this label has no effect on which candidate flows work.
    eventType: eventType || EVENT_TYPES.INTERVIEW,
    config: config || {},
    status: "ACTIVE",
    createdBy: user?.uid || null,
    candidateCount: 0,
    // Company Dashboard hiring-pipeline fields (Phase 1) — omitted entirely
    // (undefined) unless the Create Job Wizard supplies them, so legacy
    // single-step job creation is byte-for-byte unaffected.
    ...(pipelineStages ? { pipelineStages, stageConfig: stageConfig || {}, resumeCriteria: resumeCriteria || {} } : {})
  };
}

export async function createRecruiterJob(formValues, user) {
  const payload = buildJobPayload(formValues, user);
  const response = await authenticatedFetch(
    `${process.env.REACT_APP_API_BASE || "http://localhost:8000"}/recruiter/jobs`,
    {
      method: "POST",
      body: JSON.stringify(payload)
    }
  );

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Unable to create job");
  }

  return data.jobId;
}

export async function listRecruiterJobs(user) {
  const response = await authenticatedFetch(
    `${process.env.REACT_APP_API_BASE || "http://localhost:8000"}/recruiter/jobs`
  );

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Unable to load jobs");
  }

  return data.jobs || [];
}

export async function getRecruiterJob(jobId) {
  const response = await authenticatedFetch(
    `${process.env.REACT_APP_API_BASE || "http://localhost:8000"}/recruiter/jobs/${jobId}`
  );

  if (response.status === 404) {
    return null;
  }

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Unable to load job");
  }

  return data.job;
}

export async function listRecruiterJobInterviews(jobId) {
  const response = await authenticatedFetch(
    `${process.env.REACT_APP_API_BASE || "http://localhost:8000"}/recruiter/jobs/${jobId}/interviews`
  );

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Unable to load candidate interviews");
  }

  return data.interviews || [];
}

export async function listRecruiterInterviews() {
  const response = await authenticatedFetch(
    `${process.env.REACT_APP_API_BASE || "http://localhost:8000"}/recruiter/interviews`
  );

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Unable to load candidate interviews");
  }

  return data.interviews || [];
}

export async function getRecruiterInterviewReport(jobId, interviewId) {
  const response = await authenticatedFetch(
    `${process.env.REACT_APP_API_BASE || "http://localhost:8000"}/recruiter/jobs/${jobId}/interviews/${interviewId}/report`
  );

  const data = await response.json();
  if (response.status === 202) {
    return { pending: true, message: data.message || "Report is still being generated" };
  }
  if (!response.ok) {
    throw new Error(data.message || data.error || "Unable to load report");
  }

  return data.report;
}

export async function exportRecruiterInterviewReport(jobId, interviewId) {
  const response = await authenticatedFetch(
    `${process.env.REACT_APP_API_BASE || "http://localhost:8000"}/recruiter/jobs/${jobId}/interviews/${interviewId}/report/export`
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || data.error || "Unable to export report");
  }

  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition") || "";
  const match = disposition.match(/filename="?([^";]+)"?/i);
  const filename = match ? match[1] : `Vagmee_Report_${interviewId}.pdf`;

  return { blob, filename };
}

export async function getInviteJob(jobId) {
  const response = await fetch(
    `${process.env.REACT_APP_API_BASE || "http://localhost:8000"}/invite/jobs/${jobId}`
  );

  if (response.status === 404) {
    return null;
  }

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Unable to load interview link");
  }

  return data.job;
}

export async function createInviteInterview(payload) {
  const token = await getAuthToken();
  const response = await fetch(
    `${process.env.REACT_APP_API_BASE || "http://localhost:8000"}/invite/interviews`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify(payload)
    }
  );

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Unable to create interview");
  }

  return {
    interviewId: data.interviewId
  };
}

export async function updateInviteInterviewStatus(interviewId, payload) {
  const token = await getAuthToken();
  const response = await fetch(
    `${process.env.REACT_APP_API_BASE || "http://localhost:8000"}/invite/interviews/${interviewId}/status`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify(payload)
    }
  );

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Unable to update interview");
  }
}

export async function updateRecruiterJobStatus(jobId, status) {
  const response = await authenticatedFetch(
    `${process.env.REACT_APP_API_BASE || "http://localhost:8000"}/recruiter/jobs/${jobId}/status`,
    {
      method: "PATCH",
      body: JSON.stringify({ status })
    }
  );

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Unable to update job status");
  }
}

// Updates an AI-Interview job's question list after creation (MCQ/Coding use
// the subcollection endpoints below instead). `extra` may include
// { role, jdText, difficulty } to keep the job's config in sync for reuse.
export async function updateRecruiterJobQuestions(jobId, uploadedQuestions, extra = {}) {
  const response = await authenticatedFetch(
    `${process.env.REACT_APP_API_BASE || "http://localhost:8000"}/recruiter/jobs/${jobId}/questions`,
    {
      method: "PATCH",
      body: JSON.stringify({ uploadedQuestions, ...extra })
    }
  );

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Unable to update job questions");
  }
}

/* ── Company Dashboard hiring pipeline (Phase 1) ───────────────────────────
   Orchestration/aggregation only: these hit brand-new /recruiter/jobs/*
   endpoints backed by pipeline_service.py + resume_processing.py — they never
   call the MCQ/Coding candidate-facing routes below. ────────────────────── */

export const PIPELINE_STAGE_LABELS = {
  resume: "Resume Screening",
  form: "Form Collection",
  mcq: "MCQ Assessment",
  coding: "Coding Assessment",
  both: "MCQ + Coding",
  ai: "AI Interview",
  rank: "Final Ranking"
};

// Turns JD text (or a manually-typed role summary) into structured scoring
// criteria for the wizard's review step. jdText may come from extractCompanyJd().
export async function structureJdCriteria(jdText) {
  const response = await authenticatedFetch(`${API_BASE}/recruiter/jobs/pipeline/structure-jd`, {
    method: "POST",
    body: JSON.stringify({ jdText })
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Unable to extract scoring criteria");
  }
  return data.criteria;
}

// Uploads a batch of up to 50 resume files; processing happens asynchronously
// on the backend. Returns { batchId, totalFiles } immediately.
export async function uploadResumeBatch(jobId, files) {
  const token = await getAuthToken();
  const formData = new FormData();
  files.forEach((file) => formData.append("resumes", file));

  const response = await fetch(`${API_BASE}/recruiter/jobs/${jobId}/resumes`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Unable to upload resumes");
  }
  return data;
}

export async function listResumeBatches(jobId) {
  const response = await authenticatedFetch(`${API_BASE}/recruiter/jobs/${jobId}/resume-batches`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Unable to load resume batches");
  }
  return data.batches || [];
}

// sync: true first rolls MCQ/Coding/AI results into the rows and applies
// stage thresholds on the server (slower — use for initial load / refresh,
// not for background polling).
export async function listJobCandidates(jobId, { pageSize = 100, pageToken, sync = false } = {}) {
  const params = new URLSearchParams({ page_size: String(pageSize) });
  if (pageToken) params.set("page_token", pageToken);
  if (sync) params.set("sync", "true");
  const response = await authenticatedFetch(`${API_BASE}/recruiter/jobs/${jobId}/candidates?${params.toString()}`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Unable to load candidates");
  }
  return {
    candidates: data.candidates || [],
    nextPageToken: data.nextPageToken || null,
    unmatched: data.unmatched || []
  };
}

// Threshold-able metrics per stage — mirrors pipeline_sync.STAGE_METRICS on
// the backend (the server re-validates; this only drives the editor UI).
export const STAGE_THRESHOLD_METRICS = {
  resume: [["score", "Resume score"]],
  form: [],
  mcq: [["score", "MCQ score %"], ["integrity", "Integrity"]],
  coding: [["score", "Coding solved %"], ["integrity", "Integrity"]],
  both: [["mcq", "MCQ score %"], ["coding", "Coding solved %"], ["integrity", "Integrity"]],
  ai: [["overall", "Overall"], ["technical", "Technical"], ["communication", "Communication"],
       ["confidence", "Confidence"], ["integrity", "Integrity"]]
};

export async function saveStageThresholds(jobId, thresholds) {
  const response = await authenticatedFetch(`${API_BASE}/recruiter/jobs/${jobId}/pipeline/thresholds`, {
    method: "PUT",
    body: JSON.stringify({ thresholds, publicBaseUrl: window.location.origin })
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Unable to save thresholds");
  }
  return data.thresholds;
}

// Removes a single candidate row — used to clean up a failed/orphaned entry.
// (Re-uploading the same filename now reuses the failed row automatically;
// this is for cleaning up rows that were already orphaned before that fix,
// or any other row a recruiter wants gone.)
export async function deleteJobCandidate(jobId, candidateId) {
  const response = await authenticatedFetch(`${API_BASE}/recruiter/jobs/${jobId}/candidates/${candidateId}`, {
    method: "DELETE"
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Unable to remove candidate");
  }
}

// Tracking-only: moves the given candidates' pipeline `stage` field forward.
// Does NOT assign an MCQ/Coding test, send a form, or create an AI Interview
// invite — those integrations don't exist yet, so callers must label this
// "Advance", not "Send", to avoid implying candidates were notified.
export async function advanceCandidatesStage(jobId, candidateIds, stage) {
  const response = await authenticatedFetch(`${API_BASE}/recruiter/jobs/${jobId}/candidates/stage`, {
    method: "PATCH",
    body: JSON.stringify({ candidateIds, stage })
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Unable to update candidate stage");
  }
  return data.updated;
}

// Emails each selected candidate a link to the native Vagmee information form
// (/form/{candidateId}) via Resend. Unlike advanceCandidatesStage, this DOES
// notify the candidate — real email sending, the first in this app.
export async function sendFormInvites(jobId, candidateIds) {
  const response = await authenticatedFetch(`${API_BASE}/recruiter/jobs/${jobId}/candidates/send-form`, {
    method: "POST",
    body: JSON.stringify({ candidateIds, baseUrl: window.location.origin })
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Unable to send form invites");
  }
  return { sent: data.sent || [], failed: data.failed || [] };
}

// Emails each selected candidate the link to this pipeline's MCQ/Coding
// assessment job (/interview/{assessmentJobId}) — the same login-gated link
// the recruiter could otherwise copy-paste from that job's own dashboard
// card, just delivered by email like sendFormInvites.
export async function sendTestInvites(jobId, candidateIds) {
  const response = await authenticatedFetch(`${API_BASE}/recruiter/jobs/${jobId}/candidates/send-test`, {
    method: "POST",
    body: JSON.stringify({ candidateIds, baseUrl: window.location.origin })
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Unable to send test invites");
  }
  return { sent: data.sent || [], failed: data.failed || [] };
}

// Emails each selected candidate the pipeline's AI Interview link
// (/interview/{jobId} — the pipeline job itself hosts the AI Interview round).
export async function sendInterviewInvites(jobId, candidateIds) {
  const response = await authenticatedFetch(`${API_BASE}/recruiter/jobs/${jobId}/candidates/send-interview`, {
    method: "POST",
    body: JSON.stringify({ candidateIds, baseUrl: window.location.origin })
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Unable to send interview invites");
  }
  return { sent: data.sent || [], failed: data.failed || [] };
}

// Candidate-facing link for one pipeline stage. MCQ/Coding live on the
// auto-provisioned child assessment job; the AI Interview is the pipeline job
// itself. Returns null when the stage has no shareable link yet.
export async function getPipelineStageLink(jobId, stage) {
  const origin = window.location.origin;
  if (stage === "ai") return `${origin}/interview/${jobId}`;
  if (["mcq", "coding", "both"].includes(stage)) {
    const assessment = await getPipelineAssessment(jobId);
    return assessment?.assessmentJobId ? `${origin}/interview/${assessment.assessmentJobId}` : null;
  }
  return null;
}

/* ── MCQ/Coding stage: auto-provisioned assessment job ─────────────────────
   The pipeline job itself can't directly host an MCQ/Coding round (its
   eventType is always INTERVIEW) — these transparently create/find a
   separate MCQ or CODING job behind the scenes and hand back its id, which
   the caller then points the existing QuestionBankManager UI at. */

export async function getPipelineAssessment(jobId) {
  const response = await authenticatedFetch(`${API_BASE}/recruiter/jobs/${jobId}/pipeline/assessment`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Unable to load assessment");
  }
  return data.assessment;
}

export async function createPipelineAssessment(jobId, kind) {
  const response = await authenticatedFetch(`${API_BASE}/recruiter/jobs/${jobId}/pipeline/assessment`, {
    method: "POST",
    body: JSON.stringify({ kind })
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Unable to create assessment");
  }
  return data.assessment;
}

// Field types offered by the form builder — mirrors form_validation.py's
// FIELD_TYPES on the backend (the server is the source of truth; this list
// only drives which options the builder UI shows).
export const FORM_FIELD_TYPES = [
  { value: "text", label: "Short answer" },
  { value: "textarea", label: "Paragraph" },
  { value: "number", label: "Number" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "url", label: "Link / URL" },
  { value: "date", label: "Date" },
  { value: "select", label: "Dropdown" },
  { value: "radio", label: "Multiple choice" },
  { value: "checkbox", label: "Checkboxes" },
  { value: "yesno", label: "Yes / No" }
];
export const FORM_CHOICE_TYPES = new Set(["select", "radio", "checkbox"]);

/* ── Form Collection stage: recruiter-configurable field builder ──────────── */

export async function getFormFields(jobId) {
  const response = await authenticatedFetch(`${API_BASE}/recruiter/jobs/${jobId}/pipeline/form-fields`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Unable to load form fields");
  }
  return data.fields || [];
}

export async function saveFormFields(jobId, fields) {
  const response = await authenticatedFetch(`${API_BASE}/recruiter/jobs/${jobId}/pipeline/form-fields`, {
    method: "PUT",
    body: JSON.stringify({ fields })
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Unable to save form fields");
  }
  return data.fields || [];
}

/* ── Candidate-facing information form (public, no auth) ──────────────────── */

export async function getCandidateFormConfig(candidateId) {
  const response = await fetch(`${API_BASE}/public/candidate-form/${candidateId}`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Unable to load form");
  }
  return data;
}

export async function submitCandidateForm(candidateId, responses, consent) {
  const response = await fetch(`${API_BASE}/public/candidate-form/${candidateId}/submit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ responses, consent: !!consent })
  });
  const data = await response.json();
  if (!response.ok) {
    const err = new Error(data.message || data.error || "Unable to submit form");
    if (data.errors) err.fieldErrors = data.errors;
    throw err;
  }
}

/* ── Company question bank (MCQ / Coding) ──────────────────────────────── */

async function _questionRequest(path, options) {
  const response = await authenticatedFetch(`${API_BASE}${path}`, options);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.detail || data.message || "Request failed");
  }
  return data;
}

export const listMcqQuestions = (jobId) =>
  _questionRequest(`/company/jobs/${jobId}/mcq-questions`).then((d) => d.questions || []);

export const createMcqQuestion = (jobId, question) =>
  _questionRequest(`/company/jobs/${jobId}/mcq-questions`, {
    method: "POST",
    body: JSON.stringify(question)
  });

export const updateMcqQuestion = (jobId, questionId, question) =>
  _questionRequest(`/company/jobs/${jobId}/mcq-questions/${questionId}`, {
    method: "PUT",
    body: JSON.stringify(question)
  });

export const deleteMcqQuestion = (jobId, questionId) =>
  _questionRequest(`/company/jobs/${jobId}/mcq-questions/${questionId}`, { method: "DELETE" });

export const listCodingQuestions = (jobId) =>
  _questionRequest(`/company/jobs/${jobId}/coding-questions`).then((d) => d.questions || []);

export const createCodingQuestion = (jobId, question) =>
  _questionRequest(`/company/jobs/${jobId}/coding-questions`, {
    method: "POST",
    body: JSON.stringify(question)
  });

export const updateCodingQuestion = (jobId, questionId, question) =>
  _questionRequest(`/company/jobs/${jobId}/coding-questions/${questionId}`, {
    method: "PUT",
    body: JSON.stringify(question)
  });

export const deleteCodingQuestion = (jobId, questionId) =>
  _questionRequest(`/company/jobs/${jobId}/coding-questions/${questionId}`, { method: "DELETE" });

export async function uploadQuestionsPreview(jobId, kind, file) {
  const token = await getAuthToken();
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(
    `${API_BASE}/company/jobs/${jobId}/questions/upload?kind=${kind}`,
    {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData
    }
  );

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.detail || data.message || "Unable to parse file");
  }
  return data; // { parsed, errors, valid_count, error_count }
}

// Job-agnostic counterpart to uploadQuestionsPreview — parses a file into the
// same { parsed, errors, valid_count, error_count } shape before a job
// exists, so it can be merged into a local draft on the Create Job form.
export async function parseQuestionsFile(kind, file) {
  const token = await getAuthToken();
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(
    `${API_BASE}/company/jobs/questions/parse?kind=${kind}`,
    {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData
    }
  );

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.detail || data.message || "Unable to parse file");
  }
  return data;
}

export const confirmQuestionsUpload = (jobId, kind, questions) =>
  _questionRequest(`/company/jobs/${jobId}/questions/upload/confirm?kind=${kind}`, {
    method: "POST",
    body: JSON.stringify({ questions })
  });

/* ── JD upload + AI question generation ────────────────────────────────── */

export async function extractCompanyJd(file) {
  const token = await getAuthToken();
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(`${API_BASE}/company/jobs/extract-jd`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.detail || data.message || "Unable to extract text from the file");
  }
  return data; // { text, chars }
}

// params: { event_type, role, company_name, experience, jd_text, difficulty, num_questions, language }
// MCQ/CODING resolve to { parsed, errors, valid_count, error_count, meta }
// INTERVIEW resolves to { questions, meta }
export const generateCompanyQuestions = (params) =>
  _questionRequest(`/company/jobs/generate-questions`, {
    method: "POST",
    body: JSON.stringify(params)
  });

/* ── Candidate-facing MCQ/Coding test taking ───────────────────────────── */

export async function getJobMcq(jobId) {
  const response = await fetch(`${API_BASE}/company/jobs/${jobId}/mcq`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.detail || data.message || "Unable to load assessment");
  }
  return data;
}

export async function getJobCoding(jobId) {
  const response = await fetch(`${API_BASE}/company/jobs/${jobId}/coding`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.detail || data.message || "Unable to load assessment");
  }
  return data;
}

// keepalive: grading already happens server-side, synchronously, as part of
// handling this request — this just makes sure the browser still delivers
// it even if the candidate closes the tab right after clicking Submit,
// instead of aborting it mid-flight.
export const submitJobMcq = (jobId, answers, timeUsedMs) =>
  _questionRequest(`/company/jobs/${jobId}/mcq-submit`, {
    method: "POST",
    body: JSON.stringify({ answers, time_used_ms: timeUsedMs }),
    keepalive: true
  });

export const submitJobCoding = (jobId, submissions) => {
  const payload = JSON.stringify({ submissions });
  return _questionRequest(`/company/jobs/${jobId}/coding-submit`, {
    method: "POST",
    body: payload,
    keepalive: new Blob([payload]).size < 60000
  });
};

export const getJobAttemptStatus = (jobId) =>
  _questionRequest(`/company/jobs/${jobId}/my-status`);

// Recruiter-triggered manual re-grade for one candidate's coding submission —
// backs the "Regrade" button shown when a submission has a problem stuck at
// "grading_failed" (Judge0 couldn't give a real verdict the first time).
export const regradeCodingSubmission = (jobId, submissionId) =>
  _questionRequest(`/company/jobs/${jobId}/coding-submissions/${submissionId}/regrade`, {
    method: "POST"
  });

export const saveJobProctoring = (jobId, signals) =>
  _questionRequest(`/company/jobs/${jobId}/proctoring`, {
    method: "PATCH",
    body: JSON.stringify(signals)
  });

export const convertCodingLanguage = (jobId, questionId, language) =>
  _questionRequest(`/company/jobs/${jobId}/coding-questions/${questionId}/convert?language=${language}`, {
    method: "POST"
  });

export async function getJobResults(jobId) {
  const response = await authenticatedFetch(`${API_BASE}/dashboard/company/job/${jobId}`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Unable to load job results");
  }
  return data;
}

// MCQ/Coding results via the recruiter-owner auth path (company_routes.py) —
// simpler and independent of /dashboard/company's legacy access-control chain.
export const getJobResultsForRecruiter = (jobId) =>
  _questionRequest(`/company/jobs/${jobId}/results`).then((d) => d);

/**
 * Build a CSV file client-side and trigger a browser download — no backend
 * export endpoint needed. `rows` is an array of objects; `columns` is
 * [[header, (row) => cellValue], ...].
 */
export function downloadCsv(filename, columns, rows) {
  const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [
    columns.map(([header]) => escape(header)).join(","),
    ...rows.map((row) => columns.map(([, get]) => escape(get(row))).join(","))
  ];
  const blob = new Blob([lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function downloadHtmlFile(filename, html) {
  const blob = new Blob([html], { type: "text/html;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function esc(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
  ));
}

// Per-job co-branding ({ name, logo } — either may be ""). Public read: the
// candidate page loads it before sign-in-gated calls. A failure just means
// "no co-branding" (plain Vagmee header), never a broken page.
export async function getJobBranding(jobId) {
  try {
    const response = await fetch(`${API_BASE}/company/jobs/${jobId}/branding`);
    if (!response.ok) return { name: "", logo: "" };
    const data = await response.json();
    return { name: data.name || "", logo: data.logo || "" };
  } catch (_) {
    return { name: "", logo: "" };
  }
}

export const saveJobBranding = (jobId, branding) =>
  _questionRequest(`/company/jobs/${jobId}/branding`, {
    method: "PUT",
    body: JSON.stringify(branding)
  });

// Full per-candidate answer breakdown, recruiter-only (includes correct answers).
// submissionId pins this to one specific attempt — a candidate can have more
// than one submission for the same job after a recruiter-approved retake
// (see allowMcqCodingRetake); without it, the backend falls back to that
// candidate's most recent submission.
export const getCandidateAssessmentReport = (jobId, uid, submissionId) =>
  _questionRequest(`/company/jobs/${jobId}/candidates/${uid}/report${submissionId ? `?submission_id=${encodeURIComponent(submissionId)}` : ""}`);

// Clears the one-attempt-per-candidate lock for MCQ/Coding (campaign_completions
// doc), letting this candidate take the assessment again from scratch. Past
// submission records are untouched — only the lock that blocks re-entry is cleared.
export const allowMcqCodingRetake = (jobId, uid) =>
  _questionRequest(`/company/jobs/${jobId}/candidates/${uid}/retake`, { method: "DELETE" });

// AI Interview equivalent — excludes the candidate's previous interview doc
// from the duplicate-link check (their old transcript/recording/report stay
// intact) so a fresh attempt is allowed for this job.
export const allowInterviewRetake = (jobId, interviewId) =>
  _questionRequest(`/recruiter/jobs/${jobId}/interviews/${interviewId}/allow-retake`, { method: "POST" });

/**
 * Render a candidate's full report (every question, their answer, correct
 * answer for MCQ / submitted code for coding) as a self-contained, printable
 * HTML file and trigger a download. No PDF library needed — open the file
 * and use the browser's Print > Save as PDF if a PDF is wanted.
 */
export function downloadCandidateReportHtml(report) {
  const { name, email, job_title: jobTitle, mcq, coding, proctoring } = report;
  // Absolute URL — the file is opened from disk, so a relative path won't resolve.
  const logoUrl = `${window.location.origin}/Vagmee_Logo.png`;

  let body = `
    <div class="brand">
      <div class="brand-id">
        <div class="brand-row">
          <img src="${logoUrl}" alt="Vagmee" onerror="this.style.display='none'" />
          <span class="brand-name">Vagmee</span>
        </div>
        <a class="brand-url" href="https://www.vagmee.in" target="_blank" rel="noopener">www.vagmee.in</a>
      </div>
      <span class="brand-sub">Candidate Assessment Report</span>
    </div>
    <h1>${esc(jobTitle)}</h1>
    <h2>${esc(name || "Candidate")} ${email ? `&lt;${esc(email)}&gt;` : ""}</h2>
    <table class="meta">
      <tr><td>Integrity score</td><td>${proctoring.integrity_score} (${esc(proctoring.integrity_level)})</td></tr>
      <tr><td>Tab switches</td><td>${proctoring.tab_switch_count}</td></tr>
      <tr><td>Window blur events</td><td>${proctoring.window_blur_count}</td></tr>
      <tr><td>Copy/paste attempts</td><td>${proctoring.copy_paste_attempts}</td></tr>
      <tr><td>Fullscreen exits</td><td>${proctoring.fullscreen_exit_count ?? 0}</td></tr>
      <tr><td>Screen share stopped</td><td>${proctoring.screen_share_stopped_count ?? 0}</td></tr>
      <tr><td>Multiple displays detected</td><td>${proctoring.multi_display_detected ? "Yes" : "No"}</td></tr>
      <tr><td>Screen share unsupported on device</td><td>${proctoring.screen_share_unsupported ? "Yes (iOS)" : "No"}</td></tr>
      <tr><td>Looked left</td><td>${proctoring.gaze_look_left ?? 0}</td></tr>
      <tr><td>Looked right</td><td>${proctoring.gaze_look_right ?? 0}</td></tr>
      <tr><td>Looked down</td><td>${proctoring.gaze_look_down ?? 0}</td></tr>
      <tr><td>Face not visible</td><td>${proctoring.face_not_visible ?? 0}</td></tr>
      <tr><td>Multiple faces detected</td><td>${(proctoring.multiple_faces ?? 0) > 0 ? "Yes" : "No"}</td></tr>
    </table>
  `;

  if (mcq) {
    body += `
      <h3>MCQ — ${mcq.correct_answers}/${mcq.total_questions} correct (${mcq.scores?.total ?? 0}%)</h3>
      ${mcq.answers.map((a, i) => `
        <div class="q ${a.is_answered === false ? "skip" : a.is_correct ? "ok" : "bad"}">
          <p class="qtext"><strong>Q${i + 1}.</strong> ${esc(a.question)}${a.is_answered === false ? ' <span class="not-answered">Not answered</span>' : ""}</p>
          <ul>
            ${["option_a", "option_b", "option_c", "option_d"].map((key) => {
              const text = a[key];
              const isSelected = !!a.selected && text === a.selected;
              const isCorrect = text === a.correct_answer;
              const cls = isCorrect ? "correct" : isSelected ? "wrong" : "";
              return `<li class="${cls}">${esc(text)}${isSelected ? " — candidate's answer" : ""}${isCorrect ? " ✓" : ""}</li>`;
            }).join("")}
          </ul>
        </div>
      `).join("")}
    `;
  }

  if (coding?.grading_pending) {
    body += `
      <h3>Coding submissions</h3>
      <p class="pending">Submission is saved; grading is still in progress. Download the report again once grading completes.</p>
    `;
  } else if (coding) {
    body += `
      <h3>Coding submissions</h3>
      ${coding.submissions.map((s, i) => `
        <div class="q ${String(s.status).toLowerCase() === "accepted" ? "ok" : "bad"}">
          <p class="qtext"><strong>Problem ${i + 1}.</strong> ${esc(s.question)}</p>
          <p>Language: ${esc(s.language)} — Status: ${esc(s.status)}</p>
          <pre>${esc(s.code)}</pre>
        </div>
      `).join("")}
    `;
  }

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(name || "Candidate")} — ${esc(jobTitle)}</title>
    <style>
      body { font-family: -apple-system, "Segoe UI", sans-serif; max-width: 800px; margin: 2rem auto; color: #1a1a1a; }
      h1 { font-size: 1.4rem; margin-bottom: 0.25rem; }
      h2 { font-size: 1.1rem; color: #444; margin-top: 0; }
      h3 { margin-top: 2rem; border-bottom: 2px solid #ddd; padding-bottom: 0.4rem; }
      table.meta { border-collapse: collapse; margin: 1rem 0; }
      table.meta td { padding: 4px 12px 4px 0; }
      .q { margin: 1.25rem 0; padding: 0.9rem 1rem; border-radius: 8px; border: 1px solid #ddd; }
      .q.ok { background: #f0faf4; border-color: #bfe8cc; }
      .q.bad { background: #fdf3f3; border-color: #f0c6c6; }
      .q.skip { background: #f7f7f7; border-color: #ddd; }
      .not-answered { margin-left: 6px; padding: 1px 8px; border-radius: 10px; background: #e5e5e5; color: #555; font-size: 0.75rem; font-weight: 600; }
      .qtext { margin: 0 0 0.5rem; }
      ul { margin: 0; padding-left: 1.2rem; }
      li.correct { color: #0e7c3f; font-weight: 600; }
      li.wrong { color: #b3261e; font-weight: 600; }
      pre { background: #1e1e1e; color: #e5e5e5; padding: 0.75rem; border-radius: 6px; overflow-x: auto; font-size: 0.82rem; }
      .brand { display: flex; align-items: center; gap: 10px; padding-bottom: 0.75rem; margin-bottom: 1.25rem; border-bottom: 2px solid #ddd; }
      .brand-id { display: inline-flex; flex-direction: column; align-items: stretch; gap: 4px; }
      .brand-row { display: flex; align-items: center; gap: 12px; }
      .brand img { height: 56px; width: auto; }
      .brand-name { font-size: 2rem; font-weight: 700; line-height: 1; }
      .brand-url { display: block; text-align: center; color: #666; font-size: 0.9rem; letter-spacing: 0.04em; text-decoration: none; border-top: 1px solid #e5e5e5; padding-top: 4px; }
      .brand-sub { margin-left: auto; color: #666; font-size: 0.85rem; }
      .footer a { color: inherit; }
      .pending { color: #856404; background: #fff3cd; padding: 0.6rem 0.9rem; border-radius: 6px; }
      .footer { margin-top: 2.5rem; padding-top: 0.75rem; border-top: 1px solid #ddd; color: #888; font-size: 0.8rem; text-align: center; }
      @media print { .q { break-inside: avoid; } }
    </style>
  </head><body>${body}<div class="footer">Generated by <strong>Vagmee</strong> for recruiter review · <a href="https://www.vagmee.in" target="_blank" rel="noopener">www.vagmee.in</a></div></body></html>`;

  const safeName = (name || email || "candidate").replace(/[^a-z0-9]+/gi, "_");
  downloadHtmlFile(`${safeName}_report.html`, html);
}
